/* eslint-disable @typescript-eslint/dot-notation */
import { Box, Button, CircularProgress, Grid, Skeleton, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { ActionModal } from '../customComponents';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { CustomTooltip, OverflowTooltip } from '@AMIEWEB/Common/Tooltips';
import {
  IContractDoc,
  ContractStatus,
  ContractDocSource,
  isAmieWebEditableDraft,
  isAmieWebWithDocumentImage,
  isPlacementContractPdfByStatus,
  shouldShowTscPublishButton,
  shouldShowTscUnpublishButton,
  getContractDocDisplayStatus,
} from 'store/redux-store/placement-candidate-contract-doc-editor/types';
import { customSwitch } from 'utils/customSwitchCase/customSwitch';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectDocumentView,
  selectDocStatusExists,
} from 'store/redux-store/placement-candidate-contract-doc-editor/selector';
import { placementCandidateContractStatusUpdateId } from 'store/redux-store/placement-candidate-contract-doc-editor/async-actions';
import { pContractDocEditorActions } from 'store/redux-store/placement-candidate-contract-doc-editor/slice';
import { usePromiseTracker } from 'react-promise-tracker';
import { StatusBubble, UpdateWarningFlag } from '../customComponents/DocumentCard';
import { CustomTooltipProps } from '@AMIEWEB/Common/Tooltips/CustomTooltip';
import styled from 'styled-components';
import { placementStatusAction } from 'store/redux-store/placement-status/slice';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';
import { placementSummaryActions } from 'store/redux-store/placement-summary/slice';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import { selectPlacementStatus } from 'store/redux-store/placement-status/selectors';
import { isAtOrAfterBookingApproved } from '@AMIEWEB/Placement/NextPlacementStatus/StatusDefaults';
import { selectUser } from 'oidc/user.selectors';
import { userRoles } from 'oidc/userRoles';
import { Authorized } from 'oidc/userHelper';

type ContractEditorToolbarBtn = {
  children: string;
  disabled: boolean;
  onClick: () => void;
  startIcon?: JSX.Element;
  classes?: { containedSecondary: string };
  color?: 'secondary';
  tooltipProps?: CustomTooltipProps;
  toolbarItemClassName?: string;
};

const useStyles = makeStyles()(theme => ({
  container: {
    padding: '0 12px 12px 0',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  titleRoot: {
    display: 'flex',
    flexDirection: 'row',
    gap: 6,
    alignItems: 'center',
  },
  titleSkeleton: {
    width: `calc(100% - ${theme.spacing(2)})`,
    /**
     * Code: marginBottom of 17 instead of 12px -
     * ensure section doesn't hop vertically when switching to loader
     */
    marginBottom: 17,
  },
  title: {
    color: theme.palette.system.darkGray,
    fontWeight: 450,
    fontSize: 16,
    whiteSpace: 'pre',
  },
  subTitle: {
    color: theme.palette.text.primary,
    fontSize: 14,
    maxWidth: 'inherit',
  },
  noTemplateSelected: {
    fontSize: '14px',
    fontWeight: 400,
    letterSpacing: '0px',
    color: theme.palette.system.lightGrey,
    opacity: 1,
    padding: '2px 0 6px 0',
  },
  btnContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'end',
  },
  secondaryBtn: {
    backgroundColor: theme.palette.system.buttonGray,
    ':hover': {
      backgroundColor: theme.palette.system.buttonDarkGray,
    },
  },
  /** Candidate contract Delete: hidden visually but keeps logic for future use */
  toolbarDeleteHidden: {
    display: 'none',
  },
  /** Request Contract: hidden visually for all statuses; keep `requestContractBtn` in toolbar array for future use */
  toolbarRequestContractHidden: {
    display: 'none',
  },
}));

export const EngineTemplateTitle: React.FC<{
  isTemplateSelected: boolean;
  templateTitle: string | null;
  isDirty: boolean;
  userIsReadOnly: boolean;
  onCancel: (callbackFn, ...args) => void;
  onSave: () => void;
  onCreate: () => void;
  templates: IContractDoc[];
  selectedTemplate: IContractDoc;
  forceModalCloseFlag?: boolean;
  [key: string]: any;
}> = ({
  isTemplateSelected,
  templateTitle,
  isDirty,
  userIsReadOnly,
  onCancel,
  onSave,
  onCreate,
  templates = [],
  selectedTemplate,
  forceModalCloseFlag,
  ...props
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const history = useNavigate();
  const location = useLocation();
  const { placementId } = useParams<{ placementId: string }>();

  const { amieWebDraftExists, classicDraftExists, amieWebCreatedExists } = useSelector(selectDocStatusExists);
  const { renderPreviewScreen, pdfViewFromEditor, document } = useSelector(selectDocumentView);
  const { placementOrderType } = useSelector(selectPlacementDetails);
  const { currentPlacementStatus } = useSelector(selectPlacementStatus);
  const { userInfo } = useSelector(selectUser);

  /** Read-only or missing candidate contract roles (CREATE / OK to Send / Sent by TCC only). */
  const isUserDisabled = React.useMemo(
    () =>
      userIsReadOnly ||
      !Authorized(
        [userRoles.candidate_Contracts_Leadership, userRoles.candidate_Contracts_TeamMember],
        userInfo,
      ),
    [userInfo?.roles, userIsReadOnly],
  );

  const isGeneratePlacementStatusDisabled = React.useMemo(
    () => !isAtOrAfterBookingApproved(currentPlacementStatus),
    [currentPlacementStatus],
  );

  // @ts-ignore
  const [modalProps, setModalProps] = React.useState<IActionModalProps>({ open: false });
  // @ts-ignore
  const closeModal = React.useCallback(() => setModalProps({ open: false }), []);

  const dispatch = useDispatch();

  const { promiseInProgress: isPullingPdfDoc } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.requestPdfDoc,
  });
  const { promiseInProgress: isSavingingDraft } = usePromiseTracker({ area: PromiseTrackerKeys.placements.saveDraft });
  const { promiseInProgress: isDeletingDraft } = usePromiseTracker({ area: PromiseTrackerKeys.placements.deleteDraft });
  const { promiseInProgress: isCreatingDoc } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.createDocument,
  });
  const { promiseInProgress: isPublishingDoc } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.publishDocument,
  });
  const { promiseInProgress: isTscPublishingDoc } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.tscPublishDocument,
  });
  const { promiseInProgress: isVoidingDoc } = usePromiseTracker({ area: PromiseTrackerKeys.placements.voidDocument });
  const { promiseInProgress: isUpdatingContractStatus } = usePromiseTracker({
    area: PromiseTrackerKeys.placements.contractStatusUpdate,
  });

  /** Create does not prompt for classic override; that modal is Generate-only (`EngineTemplates`). */
  const handleCreate = React.useCallback(() => {
    onCreate?.();
  }, [onCreate]);

  useEffect(() => {
    closeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceModalCloseFlag]);

  const handleDeleteDraft = React.useCallback(() => {
    setModalProps({
      open: true,
      title: t('templateEditor.modal.title.deleteDraft'),
      message: t('templateEditor.modal.message.deleteDraftAction'),
      primaryBtns: [
        {
          text: t('cancelModal.confirmation.yes'),
          onClick: () => {
            dispatch(pContractDocEditorActions.deleteContractDraft({ placementId: parseInt(placementId) }));
            closeModal();
          },
        },
      ],
      secondaryBtns: [
        {
          text: t('cancelModal.confirmation.cancel'),
          onClick: closeModal,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amieWebDraftExists, classicDraftExists, placementId]);

  const handleDeletePendingDraft = React.useCallback(() => {
    setModalProps({
      open: true,
      title: t('templateEditor.modal.title.deleteDraft'),
      message: t('templateEditor.modal.message.deleteDraftAction'),
      primaryBtns: [
        {
          text: t('cancelModal.confirmation.yes'),
          onClick: () => {
            dispatch(
              pContractDocEditorActions.deleteContractDraftNew({
                placementId: parseInt(placementId),
                documentId: selectedTemplate?.documentId,
              }),
            );
            closeModal();
          },
        },
      ],
      secondaryBtns: [
        {
          text: t('cancelModal.confirmation.cancel'),
          onClick: closeModal,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementId, selectedTemplate?.documentId]);

  const handleDeleteDoc = React.useCallback(() => {
    setModalProps({
      open: true,
      title: t('templateEditor.modal.title.deleteCandidateContract'),
      message: t('templateEditor.modal.message.deleteCandidateContract'),
      primaryBtns: [
        {
          text: t('cancelModal.confirmation.yes'),
          onClick: () => {
            dispatch(
              pContractDocEditorActions.deleteContractDoc({
                placementId: parseInt(placementId),
                contractId: selectedTemplate?.id,
              }),
            );
            closeModal();
          },
        },
      ],
      secondaryBtns: [
        {
          text: t('cancelModal.confirmation.cancel'),
          onClick: closeModal,
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementId, selectedTemplate?.documentId]);

  const isCallingAPI = React.useMemo(
    () =>
      isDeletingDraft ||
      isCreatingDoc ||
      isPublishingDoc ||
      isTscPublishingDoc ||
      isVoidingDoc ||
      isSavingingDraft ||
      isUpdatingContractStatus,
    [isDeletingDraft, isCreatingDoc, isPublishingDoc, isTscPublishingDoc, isVoidingDoc, isSavingingDraft, isUpdatingContractStatus],
  );

  const pendingDeleteBtn = React.useMemo(
    () => ({
      children: t('templateEditor.delete'),
      disabled: userIsReadOnly || isCallingAPI || renderPreviewScreen,
      onClick: isDeletingDraft ? undefined : handleDeletePendingDraft,
      startIcon: isDeletingDraft ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
      tooltipProps: {
        disabled: !userIsReadOnly,
        tooltipContent: t('templateEditor.disableToolTip.accessRestriction'),
      },
    }),
    [
      handleDeletePendingDraft,
      userIsReadOnly,
      renderPreviewScreen,
      isDeletingDraft,
      isCallingAPI,
    ],
  );

  const deleteBtn = React.useMemo(() => {
    const isDeleteDraftFlow = selectedTemplate?.status === ContractStatus.draft;
    return {
      children: t('templateEditor.delete'),
      disabled:
        userIsReadOnly || isCallingAPI
          ? true
          : !(isDeleteDraftFlow || selectedTemplate?.status === ContractStatus.created) || renderPreviewScreen,
      onClick: isDeletingDraft ? undefined : isDeleteDraftFlow ? handleDeleteDraft : handleDeleteDoc,
      startIcon: isDeletingDraft ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
      tooltipProps: {
        disabled: !userIsReadOnly,
        tooltipContent: t('templateEditor.disableToolTip.accessRestriction'),
      },
      toolbarItemClassName: classes.toolbarDeleteHidden,
    };
  }, [
    classes.toolbarDeleteHidden,
    handleDeleteDoc,
    handleDeleteDraft,
    userIsReadOnly,
    renderPreviewScreen,
    selectedTemplate?.source,
    selectedTemplate?.status,
    isDeletingDraft,
    isCallingAPI,
  ]);

  const saveBtn = React.useMemo(
    () => ({
      children: t('templateEditor.save'),
      /** Enabled for a new template, as well as if dirty otherwise: Disabled if user is readonly  */
      disabled: userIsReadOnly || isCallingAPI ? true : !isDirty || renderPreviewScreen,
      onClick: isCallingAPI ? undefined : onSave,
      startIcon: isSavingingDraft ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
      tooltipProps: {
        disabled: !userIsReadOnly,
        tooltipContent: t('templateEditor.disableToolTip.accessRestriction'),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDirty, userIsReadOnly, onSave, renderPreviewScreen, isSavingingDraft, isCallingAPI],
  );

  const createBtn = React.useMemo(
    () => {
      const disabled =
        isUserDisabled || isGeneratePlacementStatusDisabled
          ? true
          : amieWebCreatedExists || !isTemplateSelected;
      return {
        children: t('templateEditor.generate'),
        disabled: disabled || isCallingAPI,
        onClick: isCallingAPI ? undefined : handleCreate,
        startIcon: isCreatingDoc ? (
          <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
        ) : undefined,
        tooltipProps: {
          disabled: !disabled,
          tooltipContent: isUserDisabled
            ? t('templateEditor.disableToolTip.userRoleRestriction')
            : isGeneratePlacementStatusDisabled
              ? t('templateEditor.disableToolTip.placementBookingApprovedRequired')
              : amieWebCreatedExists
                ? t('templateEditor.disableToolTip.createdContractExists')
                : null,
        },
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleCreate,
      isTemplateSelected,
      isUserDisabled,
      isGeneratePlacementStatusDisabled,
      isCreatingDoc,
      isCallingAPI,
    ],
  );

  const publishBtn = React.useMemo(
    () => {
      const pId = parseInt(placementId);
      return {
        children: t('templateEditor.publish'),
        disabled: userIsReadOnly || isCallingAPI,
        tooltipProps: {
          disabled: !userIsReadOnly,
          tooltipContent: t('templateEditor.disableToolTip.accessRestriction'),
        },
        onClick: () =>
          dispatch(
            pContractDocEditorActions.publishContractDoc({
              placementId: pId,
              contractId: selectedTemplate?.id,
            }),
          ),
        startIcon: isPublishingDoc ? (
          <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
        ) : undefined,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTemplate?.id, userIsReadOnly, isPublishingDoc, isCallingAPI],
  );

  const downloadBtn = React.useMemo(
    () => ({
      disabled: isCallingAPI,
      children: t('templateEditor.download'),
      onClick: () => dispatch(pContractDocEditorActions.business_DownloadSelectedContractDoc()),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCallingAPI],
  );

  const voidBtn = React.useMemo(
    () => {
      const pId = parseInt(placementId);
      return {
        children: t('templateEditor.void'),
        disabled: userIsReadOnly || isCallingAPI,
        tooltipProps: {
          disabled: !userIsReadOnly,
          tooltipContent: t('templateEditor.disableToolTip.accessRestriction'),
        },
        onClick: () =>
          dispatch(
            pContractDocEditorActions.voidContractDoc({
              placementId: pId,
              contractId: selectedTemplate?.id,
            }),
          ),
        startIcon: isVoidingDoc ? (
          <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
        ) : undefined,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTemplate?.id, userIsReadOnly, isVoidingDoc, isCallingAPI],
  );

  const requestContractBtn = React.useMemo(
    () => ({
      children: t('templateEditor.requestContract'),
      disabled: userIsReadOnly || isCallingAPI || !isTemplateSelected,
      tooltipProps: {
        disabled: !userIsReadOnly,
        tooltipContent: t('templateEditor.disableToolTip.accessRestriction'),
      },
      onClick: () =>
        dispatch(
          pContractDocEditorActions.updateContractStatus({
            placementId: parseInt(placementId),
            documentId: selectedTemplate?.documentId,
            statusId: placementCandidateContractStatusUpdateId.requestContract,
          }),
        ),
      startIcon: isUpdatingContractStatus ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
      toolbarItemClassName: classes.toolbarRequestContractHidden,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      classes.toolbarRequestContractHidden,
      dispatch,
      placementId,
      selectedTemplate?.documentId,
      userIsReadOnly,
      isCallingAPI,
      isTemplateSelected,
      isUpdatingContractStatus,
    ],
  );

  const okToSendBtn = React.useMemo(
    () => ({
      children: t('templateEditor.okToSend'),
      disabled: isUserDisabled || isCallingAPI,
      tooltipProps: {
        disabled: !isUserDisabled,
        tooltipContent: t('templateEditor.disableToolTip.accessRoleRestriction'),
      },
      onClick: () =>
        dispatch(
          pContractDocEditorActions.updateContractStatus({
            placementId: parseInt(placementId),
            documentId: selectedTemplate?.documentId,
            statusId: placementCandidateContractStatusUpdateId.okToSend,
          }),
        ),
      startIcon: isUpdatingContractStatus ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, placementId, selectedTemplate?.documentId, isUserDisabled, isCallingAPI, isUpdatingContractStatus],
  );

  const tscPublishBtn = React.useMemo(
    () => ({
      children: t('templateEditor.publish'),
      disabled: isUserDisabled || isCallingAPI,
      tooltipProps: {
        disabled: !isUserDisabled,
        tooltipContent: t('templateEditor.disableToolTip.accessRoleRestriction'),
      },
      onClick: () =>
        dispatch(
          pContractDocEditorActions.publishContractTscNew({
            placementId: parseInt(placementId),
            documentId: selectedTemplate?.documentId,
            shouldSetTsc: true,
          }),
        ),
      startIcon: isTscPublishingDoc ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, placementId, selectedTemplate?.documentId, isUserDisabled, isCallingAPI, isTscPublishingDoc],
  );

  const tscUnpublishBtn = React.useMemo(
    () => ({
      children: t('templateEditor.unpublish'),
      disabled: isUserDisabled || isCallingAPI,
      tooltipProps: {
        disabled: !isUserDisabled,
        tooltipContent: t('templateEditor.disableToolTip.accessRoleRestriction'),
      },
      onClick: () =>
        dispatch(
          pContractDocEditorActions.publishContractTscNew({
            placementId: parseInt(placementId),
            documentId: selectedTemplate?.documentId,
            shouldSetTsc: false,
          }),
        ),
      startIcon: isTscPublishingDoc ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, placementId, selectedTemplate?.documentId, isUserDisabled, isCallingAPI, isTscPublishingDoc],
  );

  const sendByTccBtn = React.useMemo(
    () => ({
      children: t('templateEditor.publish'),
      disabled: isUserDisabled || isCallingAPI,
      tooltipProps: {
        disabled: !isUserDisabled,
        tooltipContent: t('templateEditor.disableToolTip.accessRoleRestriction'),
      },
      onClick: () =>
        dispatch(
          pContractDocEditorActions.updateContractStatus({
            placementId: parseInt(placementId),
            documentId: selectedTemplate?.documentId,
            statusId: placementCandidateContractStatusUpdateId.sendByTcc,
          }),
        ),
      startIcon: isUpdatingContractStatus ? (
        <CircularProgress sx={{ color: theme => theme.palette.system.primary }} size={12} />
      ) : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, placementId, selectedTemplate?.documentId, isUserDisabled, isCallingAPI, isUpdatingContractStatus],
  );

  const cancelBtn = React.useMemo(
    (): ContractEditorToolbarBtn => ({
      children: t('templateEditor.cancel'),
      disabled: !isTemplateSelected || renderPreviewScreen || isCallingAPI,
      classes: { containedSecondary: classes.secondaryBtn },
      color: 'secondary',
      onClick: () =>
        setModalProps({
          open: true,
          title: t('cancelModal.undoConfirmation'),
          message: t('templateEditor.modal.message.discardTemplateChanges'),
          primaryBtns: [
            {
              text: t('cancelModal.confirmation.yes'),
              onClick: () => {
                onCancel(closeModal, { resetContacts: true });
              },
            },
          ],
          secondaryBtns: [
            {
              text: t('cancelModal.confirmation.cancel'),
              onClick: closeModal,
            },
          ],
        }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTemplateSelected, onCancel, renderPreviewScreen, isCallingAPI],
  );

  const closeBtn = React.useMemo(
    (): ContractEditorToolbarBtn => ({
      disabled: isCallingAPI,
      children: t('templateEditor.close'),
      classes: { containedSecondary: classes.secondaryBtn },
      color: 'secondary',
      onClick: () => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('view');
        searchParams.delete('default_selection');
        searchParams.delete('documentId');
        searchParams.delete('documentImageId');
        searchParams.delete('source');
        history(`${location.pathname}?${searchParams.toString()}`);

        /** Code: Refresh WhatsNext Container */
        dispatch(placementStatusAction.getPlacementStatusDetails({ placementId: props.placementId }));
        dispatch(placementSummaryActions.getBookingRequirement({ placementId: props.placementId, placementOrderType }));
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, props.placementId, isCallingAPI],
  );

  const btnRender = React.useMemo((): ContractEditorToolbarBtn[] => {
    /** PDF viewer toolbar: legacy Web `documentImageId` or OK to Send / Sent by TCC (Contract doc-type API). */
    const showPdfToolbarExtras =
      !!selectedTemplate &&
      (isAmieWebWithDocumentImage(selectedTemplate) || isPlacementContractPdfByStatus(selectedTemplate));

    const appendDownloadWhenContractPdf = (rows: ContractEditorToolbarBtn[]): ContractEditorToolbarBtn[] => {
      if (!showPdfToolbarExtras) return rows;
      if (rows.some(b => b === downloadBtn)) return rows;
      return [...rows, downloadBtn];
    };

    if (selectedTemplate) {
      const { status } = selectedTemplate;
      if (status === ContractStatus.pending) {
        const rest: ContractEditorToolbarBtn[] = [pendingDeleteBtn, saveBtn, createBtn, requestContractBtn];
        const lead = !isDirty ? closeBtn : cancelBtn;
        return appendDownloadWhenContractPdf([lead, ...rest]);
      }
      if (status === ContractStatus.requestContract) {
        const rest: ContractEditorToolbarBtn[] = [saveBtn, createBtn];
        const lead = !isDirty ? closeBtn : cancelBtn;
        return appendDownloadWhenContractPdf([lead, ...rest]);
      }
      if (status === ContractStatus.okToSend) {
        const rest: ContractEditorToolbarBtn[] = [sendByTccBtn];
        return appendDownloadWhenContractPdf([closeBtn, ...rest]);
      }
      if (status === ContractStatus.sentByTcc) {
        const rest: ContractEditorToolbarBtn[] = [];
        if (shouldShowTscPublishButton(selectedTemplate)) rest.push(tscPublishBtn);
        if (shouldShowTscUnpublishButton(selectedTemplate)) rest.push(tscUnpublishBtn);
        return appendDownloadWhenContractPdf([closeBtn, ...rest]);
      }
    }

    const actionTemplateStatus =
      isAmieWebEditableDraft(selectedTemplate) && selectedTemplate?.status === ContractStatus.pending
        ? ContractStatus.draft
        : selectedTemplate?.status;
    const btns: ContractEditorToolbarBtn[] = customSwitch({
      [ContractStatus.draft]: [deleteBtn, saveBtn, createBtn],
      [ContractStatus.created]:
        selectedTemplate?.source === ContractDocSource.amieWeb ? [deleteBtn, publishBtn, downloadBtn] : [downloadBtn],
      [ContractStatus.published]: selectedTemplate?.source === ContractDocSource.amieWeb
        ? [voidBtn, downloadBtn]
        : [downloadBtn],
      [ContractStatus.publishedVoid]: [downloadBtn],
      [ContractStatus.signed]: [downloadBtn],
      [ContractStatus.pending]: [],
      [ContractStatus.requestContract]: [],
      [ContractStatus.okToSend]: [],
    })([])(actionTemplateStatus) as ContractEditorToolbarBtn[];

    const defaultLeadBtn = showPdfToolbarExtras ? closeBtn : isDirty ? cancelBtn : closeBtn;
    btns.unshift(defaultLeadBtn);
    return appendDownloadWhenContractPdf(btns);
  }, [
    cancelBtn,
    closeBtn,
    createBtn,
    deleteBtn,
    downloadBtn,
    isDirty,
    okToSendBtn,
    pendingDeleteBtn,
    publishBtn,
    requestContractBtn,
    saveBtn,
    sendByTccBtn,
    tscPublishBtn,
    tscUnpublishBtn,
    selectedTemplate,
    selectedTemplate?.source,
    selectedTemplate?.status,
    voidBtn,
  ]);

  const titleDisplay = React.useMemo(
    () =>
      !isTemplateSelected ? (
        <Typography className={classes.noTemplateSelected}>{t('templateEditor.noDocSelection')}</Typography>
      ) : (
        <div className={classes.titleRoot}>
          <StatusBubble
            status={
              renderPreviewScreen ? ContractStatus.draft : getContractDocDisplayStatus(selectedTemplate)
            }
            cardType="contract-doc"
            contractDocSource={selectedTemplate?.source}
          />
          <UpdateWarningFlag
            isDraftManualEdit={selectedTemplate?.isDraftManualEdit}
            updateDateString={selectedTemplate?.view?.cardUpdateDateString}
          />
          <div className={classes.title}>{templateTitle}</div>
          {document?.documentId ? (
            <>
              <LeftBracket className={classes.subTitle} />
              {/* @ts-ignore */}
              <ResponsiveContainer numOfBtns={btnRender.length}>
                <OverflowTooltip value={document?.documentId} contentClass={classes.subTitle} />
              </ResponsiveContainer>
              <RightBracket className={classes.subTitle} />
            </>
          ) : null}
        </div>
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      btnRender.length,
      document?.documentId,
      isTemplateSelected,
      renderPreviewScreen,
      selectedTemplate?.status,
      selectedTemplate?.source,
      selectedTemplate?.isDraftManualEdit,
      selectedTemplate?.view?.cardUpdateDateString,
      templateTitle,
    ],
  );

  const lockMainToolbarForMergePreview = renderPreviewScreen || pdfViewFromEditor;

  return isPullingPdfDoc ? (
    <Skeleton classes={{ root: classes.titleSkeleton }} />
  ) : (
    <Grid container direction="row" justifyContent="space-between" classes={{ container: classes.container }}>
      <Grid item classes={{ root: cx({ [classes.titleContainer]: isTemplateSelected }) }}>
        {titleDisplay}
      </Grid>
      <Grid item classes={{ root: classes.btnContainer }}>
        <Grid container direction="row" gap={2}>
          {btnRender.map((entry, index) => {
            const { tooltipProps, toolbarItemClassName, ...btn } = entry as ContractEditorToolbarBtn;
            const buttonEl = (
              <Button
                variant="contained"
                size="small"
                type="button"
                {...btn}
                disabled={!!btn.disabled || lockMainToolbarForMergePreview}
              />
            );
            return (
              <span key={index.toString()} className={toolbarItemClassName}>
                {!!tooltipProps ? (
                  <CustomTooltip standardMargin {...tooltipProps}>
                    {buttonEl}
                  </CustomTooltip>
                ) : (
                  buttonEl
                )}
              </span>
            );
          })}
        </Grid>
      </Grid>
      <ActionModal {...modalProps} />
    </Grid>
  );
};

const getIncrementMaxWidth = (numOfBtns, defaultWidth) =>
  customSwitch({
    4: `${defaultWidth}px`,
    3: `${defaultWidth + 95}px`,
    2: `${defaultWidth + 160}px`,
    1: '100%',
  })(`${defaultWidth}px`)(numOfBtns);

const ResponsiveContainer = styled(Box)`
  @media (max-width: 1520px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 255)};
  }
  @media (max-width: 1500px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 235)};
  }
  @media (max-width: 1480px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 215)};
  }
  @media (max-width: 1460px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 195)};
  }
  @media (max-width: 1440px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 175)};
  }
  @media (max-width: 1420px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 155)};
  }
  @media (max-width: 1400px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 135)};
  }
  @media (max-width: 1380px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 115)};
  }
  @media (max-width: 1360px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 95)};
  }
  @media (max-width: 1340px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 75)};
  }
  @media (max-width: 1320px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 50)};
  }
  @media (max-width: 1300px) {
    max-width: ${props => getIncrementMaxWidth(props['numOfBtns'], 40)};
  }
  @media (max-width: 1280px) {
    max-width: 100%;
  }
`;

const LeftBracket = styled.span`
  margin-right: -6px;
  &:after {
    content: '(';
  }
`;

const RightBracket = styled.span`
  margin-left: -6px;
  &:after {
    content: ')';
  }
`;
